import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, setToken, getToken, formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, user object = authenticated
  const [user, setUser] = useState(null);

  const fetchMe = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/admin/auth/me");
      setUser(data);
    } catch {
      setToken(null);
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  useEffect(() => {
    const clearSession = () => setUser(false);
    window.addEventListener("voocly-admin-unauthorized", clearSession);
    return () => window.removeEventListener("voocly-admin-unauthorized", clearSession);
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/admin/auth/login", { email, password });
      if (data.mfa_required && !data.token) {
        // Deliberately do not create a client session before MFA succeeds.
        setToken(null);
        setUser(false);
        return {
          ok: true,
          mfaRequired: true,
          challengeId: data.challenge_id,
          enrollmentUri: data.enrollment_uri,
          enrollmentSecret: data.enrollment_secret,
        };
      }
      setToken(data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const completeMfa = async (challengeId, code) => {
    try {
      const { data } = await api.post("/admin/auth/mfa/verify", { challenge_id: challengeId, code });
      if (!data.token) throw new Error("MFA verification did not create a session.");
      setToken(data.token);
      setUser(data.user);
      return { ok: true, recoveryCodes: data.recovery_codes || [] };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const acceptInvite = async (token, password) => {
    try {
      const { data } = await api.post("/admin/auth/invitations/accept", { token, password });
      if (data.mfa_required && !data.token) {
        setToken(null);
        setUser(false);
        return {
          ok: true,
          mfaRequired: true,
          challengeId: data.challenge_id,
          enrollmentUri: data.enrollment_uri,
          enrollmentSecret: data.enrollment_secret,
        };
      }
      if (!data.token) throw new Error("Account activation did not create a session.");
      setToken(data.token);
      setUser(data.user);
      return { ok: true, recoveryCodes: data.recovery_codes || [] };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const completeRecovery = async (challengeId, recoveryCode) => {
    try {
      const { data } = await api.post("/admin/auth/mfa/recovery/verify", { challenge_id: challengeId, recovery_code: recoveryCode });
      if (!data.token) throw new Error("Recovery verification did not create a session.");
      setToken(data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/admin/auth/logout"); } catch (_e) { /* ignore */ }
    setToken(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, acceptInvite, completeMfa, completeRecovery, logout, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
